.page {
  /* Colors */
  --li-bg: #f4f2ee;
  --li-text: rgba(0, 0, 0, 0.9);
  --li-text-muted: rgba(0, 0, 0, 0.6);
  --li-surface: #fff;
  --li-border: #e0dfdc;
  --li-brand: #0a66c2;
  --li-brand-dark: #004182;
  --li-search-bg: #edf3f8;

  /* Font sizes */
  --li-font-xs: 11px;
  --li-font-sm: 12px;
  --li-font-base: 13px;
  --li-font-md: 15px;
  --li-font-lg: 18px;

  /* Sizing */
  --li-nav-icon: 22px;
  --li-avatar-size: 56px;
  --li-entry-icon: 28px;
  --li-banner-height: 48px;
  --li-search-max: 180px;
  --li-location-icon: 10px;

  /* Border radius */
  --li-radius-sm: 4px;
  --li-radius: 8px;
  --li-radius-pill: 16px;

  background: var(--li-bg);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: var(--li-font-base);
  color: var(--li-text);
  min-height: 100%;
  padding-bottom: 16px;
}

/* Nav */
.nav {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: var(--li-surface);
  border-bottom: 1px solid var(--li-border);
  position: sticky;
  top: 0;
  z-index: 1;
}

.navLogo {
  display: flex;
  color: var(--li-brand);
}

.navLogo svg {
  width: var(--li-nav-icon);
  height: var(--li-nav-icon);
}

.navSearch {
  padding: 4px 8px;
  background: var(--li-search-bg);
  border: none;
  border-radius: var(--li-radius-sm);
  color: var(--li-text-muted);
  font-size: var(--li-font-sm);
  flex: 1;
  max-width: var(--li-search-max);
}

/* Cards */
.card {
  background: var(--li-surface);
  border: 1px solid var(--li-border);
  border-radius: var(--li-radius);
  margin: 8px 16px 0;
  padding: 12px 16px;
  overflow: hidden;
}

/* Profile header card */
.banner {
  height: var(--li-banner-height);
  margin: -12px -16px 0;
  background: linear-gradient(135deg, var(--li-brand) 0%, var(--li-brand-dark) 100%);
  border-radius: var(--li-radius) var(--li-radius) 0 0;
}

.profileHeader {
  position: relative;
}

.avatarWrap {
  position: relative;
  width: var(--li-avatar-size);
  height: var(--li-avatar-size);
  margin-top: -28px;
  margin-bottom: 8px;
  background: var(--li-border);
  border-radius: 50%;
}

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid var(--li-surface);
  object-fit: cover;
}

.name {
  font-size: var(--li-font-lg);
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
}

.headline {
  color: var(--li-text-muted);
  margin: 2px 0 4px;
  font-size: var(--li-font-sm);
}

.location {
  display: flex;
  align-items: center;
  gap: 3px;
  color: var(--li-text-muted);
  font-size: var(--li-font-xs);
  margin: 0 0 8px;
}

.location svg {
  width: var(--li-location-icon);
  height: var(--li-location-icon);
}

.actions {
  display: flex;
  gap: 6px;
}

.btnPrimary {
  padding: 4px 14px;
  background: var(--li-brand);
  color: var(--li-surface);
  border-radius: var(--li-radius-pill);
  font-size: var(--li-font-sm);
  font-weight: 600;
  cursor: default;
}

.btnSecondary {
  padding: 4px 14px;
  border: 1px solid var(--li-brand);
  color: var(--li-brand);
  border-radius: var(--li-radius-pill);
  font-size: var(--li-font-sm);
  font-weight: 600;
  cursor: default;
}

/* Sections */
.sectionTitle {
  font-size: var(--li-font-md);
  font-weight: 600;
  margin: 0 0 8px;
}

.aboutText {
  color: var(--li-text);
  line-height: 1.5;
  margin: 0;
}

/* Entries */
.entryList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.entry {
  display: flex;
  gap: 8px;
}

.entryIcon {
  display: flex;
  align-items: flex-start;
  color: var(--li-text-muted);
  padding-top: 1px;
}

.entryIcon svg {
  width: var(--li-entry-icon);
  height: var(--li-entry-icon);
}

.entryTitle {
  font-weight: 600;
  margin: 0;
  font-size: var(--li-font-base);
}

.entrySubtitle {
  color: var(--li-text);
  margin: 1px 0 0;
  font-size: var(--li-font-sm);
}

.entryDate {
  color: var(--li-text-muted);
  margin: 1px 0 0;
  font-size: var(--li-font-xs);
}

/* Skills */
.skillList {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skillItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid var(--li-bg);
}

.skillItem:last-child {
  border-bottom: none;
}

.skillName {
  font-weight: 600;
  font-size: var(--li-font-base);
}

.skillCount {
  color: var(--li-text-muted);
  font-size: var(--li-font-xs);
}

.seeAll {
  display: block;
  width: 100%;
  padding: 8px 0 0;
  border: none;
  background: none;
  color: var(--li-text-muted);
  font-size: var(--li-font-sm);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.seeAll:hover {
  color: var(--li-brand);
}
